// DailyAffirmations, shows 3 personalized affirmations from today's insight.
// Reads from user_transit_insights for the current day.
// Used on: Dashboard (collapsible), ForecastPage (below reading).

import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface Props {
  userId: string;
  collapsible?: boolean;  // default false, when true shows expand/collapse toggle
}

export function DailyAffirmations({ userId, collapsible = false }: Props) {
  const [affirmations, setAffirmations] = useState<string[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [expanded,     setExpanded]     = useState(!collapsible);

  useEffect(() => {
    if (!userId) return;

    const today = new Date().toISOString().slice(0, 10);

    supabase
      .from('user_transit_insights')
      .select('affirmations')
      .eq('user_id', userId)
      .eq('insight_date', today)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!error && data?.affirmations && Array.isArray(data.affirmations)) {
          setAffirmations(data.affirmations.slice(0, 3));
        }
        setLoading(false);
      });
  }, [userId]);

  if (loading || affirmations.length === 0) return null;

  return (
    <div
      className="card"
      style={{
        background: 'linear-gradient(135deg, rgba(31,78,95,0.08) 0%, rgba(31,78,95,0.03) 100%)',
        border: '1px solid rgba(31,78,95,0.25)',
        borderRadius: 'var(--radius-lg)',
        padding: '1.25rem',
        marginBottom: '1rem',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: expanded ? '1rem' : 0,
          cursor: collapsible ? 'pointer' : 'default',
        }}
        onClick={collapsible ? () => setExpanded(e => !e) : undefined}
        role={collapsible ? 'button' : undefined}
        aria-expanded={collapsible ? expanded : undefined}
      >
        <h3
          className="font-display"
          style={{ fontSize: '1.05rem', color: 'var(--petrol)', margin: 0 }}
        >
          ✦ Afirmações de Hoje
        </h3>
        {collapsible && (
          <span
            style={{
              color: 'var(--petrol)',
              fontSize: '0.8rem',
              transition: 'transform 0.2s',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              display: 'inline-block',
            }}
          >
            ▾
          </span>
        )}
      </div>

      {/* Affirmation list */}
      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {affirmations.map((text, i) => (
            <div
              key={i}
              style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}
            >
              <span
                className="font-display"
                style={{
                  color: 'var(--petrol)',
                  fontSize: '1.1rem',
                  lineHeight: 1.4,
                  flexShrink: 0,
                  minWidth: '1.2rem',
                }}
              >
                {i + 1}.
              </span>
              <p
                className="reading-text"
                style={{
                  color: 'var(--text-primary)',
                  margin: 0,
                  lineHeight: 1.6,
                  fontSize: 'var(--text-body)',
                }}
              >
                {text}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
